import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import { useAppMessage } from '../../components/AppMessage';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import {
  BookOpen,
  Plus,
  Upload,
  FileText,
  Trash2,
  Search,
  Loader2,
  CheckCircle,
  AlertCircle,
  Database,
  Sparkles,
  Clock,
  XCircle,
  Link as LinkIcon,
  RefreshCw,
  Download,
  Building2,
  Users
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  processing: 'bg-blue-100 text-blue-700 border-blue-200',
  completed: 'bg-green-100 text-green-700 border-green-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
};

const statusIcons = {
  pending: <Clock className="h-4 w-4" />,
  processing: <Loader2 className="h-4 w-4 animate-spin" />,
  completed: <CheckCircle className="h-4 w-4" />,
  failed: <XCircle className="h-4 w-4" />,
};

export default function KnowledgeBasePage() {
  const msg = useAppMessage();
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [selectedKB, setSelectedKB] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [urlUploadDialogOpen, setUrlUploadDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState(() => { try { return sessionStorage.getItem('jtcrm_kb_tab') || 'documents'; } catch { return 'documents'; } });
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatCategory, setChatCategory] = useState('jobtalk');
  const [chatLoading, setChatLoading] = useState(false);
  
  // Create KB form
  const [kbName, setKbName] = useState('');
  const [kbDescription, setKbDescription] = useState('');
  const [kbCategory, setKbCategory] = useState('jobtalk');
  const [creating, setCreating] = useState(false);
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  
  // URL upload state
  const [documentUrl, setDocumentUrl] = useState('');
  const [urlUploading, setUrlUploading] = useState(false);
  
  // Regenerate state
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    fetchKnowledgeBases();
  }, []);

  useEffect(() => { sessionStorage.setItem('jtcrm_kb_tab', activeTab); }, [activeTab]);

  useEffect(() => {
    if (selectedKB) {
      fetchDocuments();
    }
  }, [selectedKB]);

  const fetchKnowledgeBases = async () => {
    try {
      const response = await axios.get(`${API}/api/knowledge-base`);
      setKnowledgeBases(response.data);
      if (response.data.length > 0 && !selectedKB) {
        setSelectedKB(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to load knowledge bases');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    if (!selectedKB) return;
    
    try {
      const response = await axios.get(`${API}/api/knowledge-base/${selectedKB.id}/documents`);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    }
  };

  const handleCreateKB = async () => {
    if (!kbName.trim()) {
      toast.error('Knowledge Base name is required');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/api/knowledge-base`, {
        name: kbName,
        description: kbDescription,
        category: kbCategory,
        embedding_provider: 'openai',
        embedding_model: 'text-embedding-3-small',
        chunk_size: 1000,
        chunk_overlap: 200
      });
      
      toast.success('Knowledge Base created successfully');
      setKnowledgeBases([...knowledgeBases, response.data]);
      setSelectedKB(response.data);
      setCreateDialogOpen(false);
      setKbName('');
      setKbDescription('');
      setKbCategory('jobtalk');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Knowledge Base');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteKB = async (kbId) => {
    if (!(await msg.confirm('Delete this Knowledge Base?', 'This will delete the knowledge base and all its documents.', { confirmLabel: 'Delete', destructive: true }))) {
      return;
    }

    try {
      await axios.delete(`${API}/api/knowledge-base/${kbId}`);
      toast.success('Knowledge Base deleted');
      setKnowledgeBases(knowledgeBases.filter(kb => kb.id !== kbId));
      if (selectedKB?.id === kbId) {
        setSelectedKB(knowledgeBases[0] || null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete Knowledge Base');
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    setSelectedFiles(acceptedFiles);
    setUploadDialogOpen(true);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    multiple: true,
  });

  const handleUploadDocuments = async () => {
    if (!selectedKB) {
      toast.error('Please select a Knowledge Base first');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const totalFiles = selectedFiles.length;
      let uploadedCount = 0;

      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);

        await axios.post(
          `${API}/api/knowledge-base/${selectedKB.id}/documents`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        uploadedCount++;
        setUploadProgress(Math.round((uploadedCount / totalFiles) * 100));
      }

      toast.success(`${uploadedCount} document(s) uploaded and processing`);
      setUploadDialogOpen(false);
      setSelectedFiles([]);
      
      setTimeout(() => fetchDocuments(), 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload documents');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleUploadFromUrl = async () => {
    if (!documentUrl.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    if (!selectedKB) {
      toast.error('Please select a Knowledge Base first');
      return;
    }

    setUrlUploading(true);
    try {
      await axios.post(`${API}/api/knowledge-base/${selectedKB.id}/documents/from-url`, {
        url: documentUrl
      });

      toast.success('Document downloaded and processing');
      setUrlUploadDialogOpen(false);
      setDocumentUrl('');
      
      setTimeout(() => fetchDocuments(), 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload from URL');
    } finally {
      setUrlUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!(await msg.confirm('Delete this document?', '', { confirmLabel: 'Delete', destructive: true }))) return;

    try {
      await axios.delete(`${API}/api/knowledge-base/${selectedKB.id}/documents/${docId}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleRegenerateEmbeddings = async (docId) => {
    if (!(await msg.confirm('Regenerate embeddings for this document?', ''))) return;

    setRegenerating(true);
    try {
      await axios.post(`${API}/api/knowledge-base/${selectedKB.id}/documents/${docId}/regenerate`);
      toast.success('Regenerating embeddings...');
      setTimeout(() => fetchDocuments(), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate embeddings');
    } finally {
      setRegenerating(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    if (!selectedKB) {
      toast.error('Please select a Knowledge Base first');
      return;
    }

    setSearching(true);
    try {
      const response = await axios.post(`${API}/api/knowledge-base/search`, {
        query: searchQuery,
        knowledge_base_ids: [selectedKB.id],
        top_k: 5,
        similarity_threshold: 0.3
      });

      setSearchResults(response.data);
      if (response.data.length === 0) {
        toast.info('No relevant results found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <BookOpen className="h-8 w-8 text-primary" />
            Knowledge Base
          </h1>
          <p className="text-slate-600 mt-1">
            Upload documents and use AI-powered semantic search
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Knowledge Base
        </Button>
      </div>

      {knowledgeBases.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Database className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Knowledge Bases Yet</h3>
            <p className="text-slate-500 mb-4">Create your first knowledge base</p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Knowledge Base
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* KB Selector */}
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="text-sm font-medium">Active Knowledge Base:</Label>
            <div className="flex gap-2 flex-wrap">
              {knowledgeBases.map(kb => (
                <Button
                  key={kb.id}
                  variant={selectedKB?.id === kb.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedKB(kb)}
                >
                  <Database className="mr-2 h-4 w-4" />
                  {kb.name}
                  <Badge variant="secondary" className="ml-2">
                    {kb.document_count || 0}
                  </Badge>
                </Button>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full max-w-2xl grid-cols-3">
              <TabsTrigger value="documents">
                <FileText className="h-4 w-4 mr-2" />
                Documents
                <Badge variant="secondary" className="ml-2">{documents.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="search">
                <Search className="h-4 w-4 mr-2" />
                Search
              </TabsTrigger>
              <TabsTrigger value="chat">
                <Sparkles className="h-4 w-4 mr-2" />
                AI Chat
              </TabsTrigger>
            </TabsList>

            {/* Documents Tab */}
            <TabsContent value="documents" className="space-y-4 mt-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Upload Documents</CardTitle>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setUrlUploadDialogOpen(true)}
                      >
                        <LinkIcon className="mr-2 h-4 w-4" />
                        Import from URL
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchDocuments}
                      >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Refresh
                      </Button>
                      {selectedKB && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteKB(selectedKB.id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete KB
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
                      isDragActive
                        ? 'border-primary bg-primary/5'
                        : 'border-slate-300 hover:border-primary/50 hover:bg-slate-50'
                    }`}
                  >
                    <input {...getInputProps()} />
                    <Upload className={`mx-auto mb-4 ${isDragActive ? 'text-primary' : 'text-slate-400'}`} size={48} />
                    <p className="text-slate-900 font-medium text-lg">
                      {isDragActive ? 'Drop files here...' : 'Drag & drop documents here'}
                    </p>
                    <p className="text-slate-600 mt-1">or click to browse files</p>
                    <p className="text-slate-500 text-sm mt-2">Supported: PDF, TXT, DOCX, HTML</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Documents ({documents.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {documents.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                      <p>No documents uploaded yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {documents.map(doc => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <FileText className="h-5 w-5 text-blue-600" />
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">{doc.filename}</p>
                              <div className="flex items-center gap-3 text-sm text-slate-600 mt-1">
                                <span>{doc.file_type.toUpperCase()}</span>
                                <span>•</span>
                                <span>{(doc.file_size / 1024).toFixed(1)} KB</span>
                                {doc.chunk_count > 0 && (
                                  <>
                                    <span>•</span>
                                    <span>{doc.chunk_count} chunks</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <Badge variant="outline" className={statusColors[doc.status]}>
                              {statusIcons[doc.status]}
                              <span className="ml-1">{doc.status}</span>
                            </Badge>
                          </div>
                          <div className="flex gap-1 ml-2">
                            {doc.status === 'completed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRegenerateEmbeddings(doc.id)}
                                className="text-blue-600"
                                disabled={regenerating}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDocument(doc.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Search Tab */}
            <TabsContent value="search" className="space-y-4 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Semantic Search</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Ask a question..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button onClick={handleSearch} disabled={searching}>
                      {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </div>

                  {searchResults && searchResults.length > 0 && (
                    <div className="space-y-3">
                      {searchResults.map((result) => (
                        <Card key={result.chunk_id}>
                          <CardContent className="pt-4">
                            <div className="flex justify-between mb-2">
                              <span className="font-medium">{result.document_title}</span>
                              <Badge>{(result.similarity_score * 100).toFixed(1)}% match</Badge>
                            </div>
                            <p className="text-sm text-slate-700">{result.content}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Chat Tab */}
            <TabsContent value="chat" className="space-y-4 mt-6">
              <Card className="h-[600px] flex flex-col">
                <CardHeader>
                  <div className="flex justify-between">
                    <CardTitle>AI Chat</CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant={chatCategory === 'jobtalk' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setChatCategory('jobtalk')}
                      >
                        <Building2 className="h-4 w-4 mr-1" />
                        JobTalk
                      </Button>
                      <Button
                        variant={chatCategory === 'franchise' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setChatCategory('franchise')}
                      >
                        <Users className="h-4 w-4 mr-1" />
                        Franchise
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {chatMessages.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <Sparkles className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                        <p>Ask questions about {chatCategory === 'jobtalk' ? 'JobTalk' : 'Franchise'} documents</p>
                      </div>
                    ) : (
                      chatMessages.map((msg, idx) => (
                        <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] rounded-lg p-3 ${
                            msg.role === 'user' ? 'bg-primary text-white' : 'bg-slate-100 text-slate-900'
                          }`}>
                            <p className="text-sm">{msg.content}</p>
                            {msg.sources && (
                              <div className="mt-2 pt-2 border-t">
                                <p className="text-xs font-medium">Sources:</p>
                                {msg.sources.map((s, i) => (
                                  <p key={i} className="text-xs">• {s}</p>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="border-t p-4">
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        if (!chatInput.trim()) return;

                        const msg = chatInput.trim();
                        setChatInput('');
                        setChatMessages([...chatMessages, { role: 'user', content: msg }]);
                        setChatLoading(true);

                        try {
                          const res = await axios.post(`${API}/api/knowledge-base/chat`, {
                            query: msg,
                            category: chatCategory
                          });
                          setChatMessages(prev => [...prev, {
                            role: 'assistant',
                            content: res.data.answer,
                            sources: res.data.sources
                          }]);
                        } catch {
                          toast.error('Failed to get AI response');
                        } finally {
                          setChatLoading(false);
                        }
                      }}
                      className="flex gap-2"
                    >
                      <Input
                        placeholder="Ask a question..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        disabled={chatLoading}
                      />
                      <Button type="submit" disabled={chatLoading}>
                        {chatLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
                      </Button>
                    </form>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Create KB Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Knowledge Base</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant={kbCategory === 'jobtalk' ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => setKbCategory('jobtalk')}
                >
                  <Building2 className="mr-2 h-4 w-4" />
                  JobTalk
                </Button>
                <Button
                  type="button"
                  variant={kbCategory === 'franchise' ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => setKbCategory('franchise')}
                >
                  <Users className="mr-2 h-4 w-4" />
                  Franchise
                </Button>
              </div>
            </div>
            <div>
              <Label>Name *</Label>
              <Input
                placeholder="e.g., Product Documentation"
                value={kbName}
                onChange={(e) => setKbName(e.target.value)}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="What documents will this contain?"
                value={kbDescription}
                onChange={(e) => setKbDescription(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateKB} disabled={creating}>
              {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Documents</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedFiles.map((file, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-medium text-sm">{file.name}</p>
                  <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
            ))}
            {uploading && <Progress value={uploadProgress} />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUploadDocuments} disabled={uploading}>
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* URL Upload Dialog */}
      <Dialog open={urlUploadDialogOpen} onOpenChange={setUrlUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import from URL</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Document URL</Label>
              <Input
                type="url"
                placeholder="https://example.com/document.pdf"
                value={documentUrl}
                onChange={(e) => setDocumentUrl(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUrlUploadDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUploadFromUrl} disabled={urlUploading}>
              {urlUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
